import { createSelector } from "@reduxjs/toolkit";
import { omit } from "lodash";

import { Store, UserAccountAllocationResponse, UserRole } from "../types";

import { selectCurrentAccount } from "./accountSlice";
import { apiSlice } from "./apiSlice";
import { selectUserMeAccountsResult } from "./userSlice";

export type UserAccountAllocationRequest = {
  ID: string;
  Role?: UserRole;
  License?: boolean;
  JobTitle?: string | null;
};

export const allocationSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    updateAllocation: build.mutation<UserAccountAllocationResponse, UserAccountAllocationRequest>({
      query: (patchAllocation) => ({
        url: `/v2/allocations/${patchAllocation.ID}`,
        method: "PATCH",
        body: omit(patchAllocation, "ID"),
      }),
      invalidatesTags: (result, error, arg) => [{ type: "UserAccountAllocation" as const, id: arg.ID }],
    }),
    requestLicenseAllocation: build.mutation({
      query: (allocationId: string) => ({
        url: `/v2/allocations/${allocationId}/request-license`,
        method: "POST",
      }),
    }),
    deleteAllocation: build.mutation({
      query: (allocationId: string) => ({
        url: `/v2/allocations/${allocationId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "UserAccountAllocation", id: arg },
        { type: "Project", id: "LIST" },
        { type: "MultiProject", id: "LIST" },
        { type: "Report", id: "LIST" },
        { type: "Folder", id: "LIST" },
      ],
    }),
  }),
});

/**
 * Endpoint Hooks
 */
export const { useUpdateAllocationMutation, useRequestLicenseAllocationMutation, useDeleteAllocationMutation } = allocationSlice;
export const selectCurrentAllocation = createSelector([(state: Store) => state, selectUserMeAccountsResult], (state: Store, result) => {
  if (!result?.data) {
    return null;
  }
  const account = selectCurrentAccount(state);
  return account ? result.data.find((allocation) => allocation.Account.ID === account.ID) : undefined;
});

export const selectCurrentRole = createSelector(selectCurrentAllocation, (allocation) => allocation?.Role);
